/**
 * B709 leg 2 — the member portal, `/me/membership` (Figma 643:2 @390), on
 * B632's signed-in shell. Faces only: `lib/meMembership.ts` shapes the reads,
 * `useMeMembership.ts` makes them. Built from the PNG; redline pending.
 * B703 (a): RENEW EARLY (`useMemberRenew.ts`) — the frame's UPGRADE and
 * "Change card ›" have no buyer door and stay out.
 */
import type { ReactElement } from "react";
import { Banner, Button, EmptyState, ProgressBar, Skeleton, StageChip, Switch } from "@transact/ui";
import type { MembershipFace, PortalRow } from "../lib/meMembership.js";
import { MeShell } from "./mePage.js";
import type { MemberRenewing } from "./useMemberRenew.js";

function Rows(props: { readonly rows: readonly PortalRow[] }): ReactElement {
  return (
    <dl className="me-mrows">
      {props.rows.map((r) => (
        <div className="me-mrow" key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Membership(props: { readonly face: MembershipFace; readonly onRenew?: ((memberNumber: string) => void) | undefined; readonly renewing?: MemberRenewing | undefined }): ReactElement {
  const { face } = props;
  const renewing = props.renewing?.memberNumber === face.memberNumber ? props.renewing : undefined;
  const door = face.renewal?.renew;
  const id = `me-m-${face.memberNumber}`;
  return (
    <>
      <section className="me-mcard" aria-labelledby={`${id}-tier`} data-redline="me-mcard">
        <div className="me-mcard-head">
          <h2 className="me-mcard-tier" id={`${id}-tier`}>
            {face.tierName}
          </h2>
          <StageChip tone={face.chip.tone}>{face.chip.word}</StageChip>
        </div>
        <p className="me-mcard-facts">{face.facts}</p>
        {face.cards.length > 0 ? (
          <ul className="me-mcard-cards">
            {face.cards.map((c) => (
              <li className="me-mcard-holder" key={c.cardId}>
                <span className="me-mcard-tile" aria-hidden="true">
                  {c.name.slice(0, 1)}
                </span>
                <p className="me-mcard-name">{c.name}</p>
                <p className="me-mcard-note">{c.note}</p>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="me-mnote">{face.cards.length > 0 ? face.cardsNote : "Your cards aren't printed yet — any ticket window issues them."}</p>
      </section>
      {face.allowances.length > 0 ? (
        <section className="me-panel me-mpanel" aria-labelledby={`${id}-allow`} data-redline="me-mallowances">
          <h2 className="me-section-label" id={`${id}-allow`}>
            {face.allowancesLabel}
          </h2>
          {face.allowances.map((a) =>
            a.fraction !== undefined ? (
              <ProgressBar key={a.label} size="meter" label={a.label} fraction={a.fraction} detail={a.words} />
            ) : (
              <Rows key={a.label} rows={[{ label: a.label, value: a.words }]} />
            ),
          )}
          {face.allowancesNote !== undefined ? <p className="me-mnote">{face.allowancesNote}</p> : null}
        </section>
      ) : null}
      {face.renewal !== undefined ? (
        <section className="me-panel me-mpanel" aria-labelledby={`${id}-renew`} data-redline="me-mrenewal">
          <h2 className="me-section-label" id={`${id}-renew`}>
            Renewal
          </h2>
          <Switch
            id={`${id}-auto`}
            label="Renew automatically"
            checked={face.renewal.auto}
            /* dead-ok: shut with its reason (FORMS R15) — no buyer door turns auto-renew on or off yet */
            onChange={() => undefined}
            disabledWhy={face.renewal.shutWhy}
          />
          <Rows rows={face.renewal.rows} />
          <p className="me-mnote">{face.renewal.note}</p>
          {door !== undefined && props.onRenew !== undefined ? (
            <div className="me-mrenew" data-redline="me-mrenew">
              <Button variant="primary" busy={renewing?.busy === true} onClick={() => props.onRenew?.(face.memberNumber)}>
                {door.label}
              </Button>
              <p className="me-mnote">{door.note}</p>
            </div>
          ) : null}
          {renewing?.banner !== undefined ? <Banner tone={renewing.banner.tone}>{renewing.banner.text}</Banner> : null}
        </section>
      ) : null}
      <section className="me-panel me-mpanel" aria-labelledby={`${id}-house`} data-redline="me-mhousehold">
        <h2 className="me-section-label" id={`${id}-house`}>
          Household
        </h2>
        <Rows rows={face.household.rows} />
        <p className="me-mnote">{face.household.note}</p>
      </section>
    </>
  );
}

export function MeMembershipPage(props: {
  readonly email: string;
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  /** "Priya" — the first named adult; absent = "Your membership". */
  readonly greeting?: string | undefined;
  /** undefined = still reading. */
  readonly faces: readonly MembershipFace[] | undefined;
  readonly error?: string | undefined;
  readonly ordersHref: string;
  /** The memberships on sale (`/memberships?tenant=`), for an email with none. */
  readonly joinHref: string;
  readonly onSignOut: () => void;
  readonly onRetry?: (() => void) | undefined;
  /** B703 (a) — the RENEW door; absent = no button (never a dead control). */
  readonly onRenew?: ((memberNumber: string) => void) | undefined;
  readonly renewing?: MemberRenewing | undefined;
}): ReactElement {
  const { faces } = props;
  return (
    <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label="Your membership" here="My membership">
      <div className="me-who" data-redline="me-who">
        <p className="me-email" data-redline="me-email">{`Signed in as ${props.email}`}</p>
        {/* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */}
        <Button variant="textlink-accent" href={props.ordersHref} onClick={() => undefined}>
          My orders ›
        </Button>
      </div>
      <h1 className="me-title me-title--member" data-redline="me-title">
        {props.greeting !== undefined ? `Hi ${props.greeting}` : "Your membership"}
      </h1>
      {props.error !== undefined ? (
        <Banner tone="warn">
          {props.error}
          {props.onRetry !== undefined ? (
            <Button variant="textlink" onClick={props.onRetry}>
              Try again
            </Button>
          ) : null}
        </Banner>
      ) : null}
      {faces === undefined ? (
        props.error !== undefined ? null : (
          <div role="status" aria-label="Reading your membership">
            <Skeleton lines={8} />
          </div>
        )
      ) : faces.length === 0 ? (
        <EmptyState
          title={`No membership under ${props.email} yet`}
          body="Memberships bought with this email show here. Bought with another address? Sign out and use that one."
          action={<a href={props.joinHref}>See memberships</a>}
        />
      ) : (
        <div className="me-mstack">
          {faces.map((f) => (
            <Membership key={f.memberNumber} face={f} onRenew={props.onRenew} renewing={props.renewing} />
          ))}
        </div>
      )}
      <p className="me-foot" data-redline="me-foot">
        Only memberships bought with this email.{" "}
        <Button variant="textlink" onClick={props.onSignOut}>
          Sign out
        </Button>
      </p>
    </MeShell>
  );
}
